<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: string[] | null;
  size?: FieldSize;
  fetchItems?: (searchPattern: string) => Promise<string[]>;
  loading?: boolean;
  minSearchLength?: number;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  fetchItems: async () => {
    return await Promise.resolve([]);
  },
  loading: false,
  minSearchLength: 1,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

const items = ref<readonly string[]>([]);
const isFetching = ref(false);

function fetchWrapper(): void {
  const searchPattern = search.value;
  isFetching.value = true;
  void props
    .fetchItems(searchPattern)
    .then((fetchedItems) => {
      if (searchPattern !== search.value) return;
      items.value = fetchedItems;
    })
    .finally(() => {
      isFetching.value = false;
    });
}

const debounceFetchWrapper = useDebounceFn(fetchWrapper, 150);

function getSearchString(search?: string | null): string {
  return (search?.trim() || '').toUpperCase();
}

watch(search, (newSearch?: string | null, oldSearch?: string | null) => {
  // console.debug('fieldValue.value', fieldValue.value);
  // console.debug('newSearch', newSearch);
  // console.debug('oldSearch', oldSearch);
  if (newSearch === null && fieldValue.value) {
    fieldValue.value = [];
    return;
  }
  newSearch = getSearchString(newSearch);
  oldSearch = getSearchString(oldSearch);
  // const value = getSearchString(fieldValue.value);
  if (newSearch.length >= props.minSearchLength) {
    if (newSearch !== oldSearch) {
      // if (newSearch !== value || oldSearch !== '') {
      void debounceFetchWrapper();
      // }
    }
    // } else if (newSearch === '' && oldSearch === value) {
  } else {
    items.value = [];
  }
});

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    :items="items"
    :class="[
      'g-v-text-field',
      $style.kFetchMultipleAutocompleteTextField,
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    :loading="props.loading || isFetching"
    no-filter
    multiple
    chips
    closable-chips
    data-testid="kFetchMultipleAutocompleteTextField"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem>
        <template v-if="!search || search.length < props.minSearchLength">
          Please enter {{ props.minSearchLength }} or more characters
        </template>
        <template v-else>No such data</template>
      </NoDataSelectVListItem>
    </template>
    <template #chip="itemData">
      <v-chip
        :class="$style.chip"
        variant="flat"
        :color="$style.chipColor"
        v-bind="itemData.props"
        :prepend-icon="itemData.item.props.titleIcon"
        label
      >
      </v-chip>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="itemData.props"
      >
        <template #prepend="{ isSelected }">
          <v-list-item-action>
            <KCheckboxField
              tabindex="-1"
              :model-value="isSelected"
              hide-details
            />
          </v-list-item-action>
        </template>
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
  chipColor: colors.$color-brand-200;
}

.kFetchMultipleAutocompleteTextField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.chip {
  color: colors.$color-text-primary !important;

  :global {
    .v-chip__prepend {
      font-size: 10px;
    }
    .v-chip__content {
      font-size: 16px;
    }
    .v-chip__close {
      background: transparent;
      border: 0;
      margin: 0;
      padding: 0;
      font-size: 16px;
    }
  }
}
</style>
